"use client";

import Cookies from "js-cookie";
import { firebaseManager } from "@/lib/firebase";

/**
 * Cookie name for storing the Firebase auth token
 */
export const AUTH_TOKEN_COOKIE = "fu-auth-token";

/**
 * Cookie expiration in days (Firebase tokens expire in 1 hour, but we use a longer cookie
 * and rely on Firebase SDK's automatic refresh)
 */
const COOKIE_EXPIRATION_DAYS = 7;

/**
 * Clears all authentication data from the browser
 * Adapted from Ballon d'Or but works with our tenant-aware Firebase manager
 */
export const clearAllAuthData = async (): Promise<void> => {
    try {
        // Remove auth token cookie
        Cookies.remove(AUTH_TOKEN_COOKIE);

        // Sign out from Firebase if initialized
        const auth = firebaseManager.getAuth();
        if (auth) {
            await auth.signOut();
        }

        // Clear Firebase-related localStorage items
        if (typeof window !== "undefined") {
            Object.keys(localStorage).forEach((key) => {
                if (key.startsWith("firebase:")) {
                    localStorage.removeItem(key);
                }
            });

            // Clear Firebase-related sessionStorage items
            Object.keys(sessionStorage).forEach((key) => {
                if (key.startsWith("firebase:")) {
                    sessionStorage.removeItem(key);
                }
            });
        }
    } catch (error) {
        console.warn("Error clearing auth data:", error);
    }
};

/**
 * Gets the current auth token from cookie
 */
export const getAuthToken = (): string | undefined => {
    return Cookies.get(AUTH_TOKEN_COOKIE);
};

/**
 * Sets the auth token in cookie
 */
export const setAuthToken = (token: string): void => {
    Cookies.set(AUTH_TOKEN_COOKIE, token, {
        expires: COOKIE_EXPIRATION_DAYS,
        secure: process.env.NODE_ENV === "production",
        sameSite: "lax",
    });
};

/**
 * Removes the auth token cookie
 */
export const removeAuthToken = (): void => {
    Cookies.remove(AUTH_TOKEN_COOKIE);
};

/**
 * Checks if there's a valid auth token in cookies
 */
export const hasAuthToken = (): boolean => {
    return !!getAuthToken();
};

/**
 * Refreshes the auth token if we have an authenticated user
 * This should be called periodically or before making authenticated API calls
 */
export const refreshAuthToken = async (): Promise<string | null> => {
    const auth = firebaseManager.getAuth();
    if (!auth?.currentUser) {
        return null;
    }

    try {
        const token = await auth.currentUser.getIdToken(true);
        setAuthToken(token);
        return token;
    } catch (error) {
        console.warn("Error refreshing auth token:", error);
        return null;
    }
};

